import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Trash2, FileDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  date: '', current_mileage: '', distance_covered: '',
  origin_address: '', destination_address: '', purpose: '', driver_name: '',
};

const AssetMileage = ({ asset, isAdmin, onRefresh }) => {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.current_mileage || !form.distance_covered) {
      toast.error(t('inv.mileage.requiredFields'));
      return;
    }
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/mileage`, {
        ...form,
        current_mileage: parseFloat(form.current_mileage),
        distance_covered: parseFloat(form.distance_covered),
      });
      toast.success(t('inv.mileage.added'));
      setAddOpen(false);
      setForm(EMPTY);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.failed')); }
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/mileage/${recordId}`);
      toast.success(t('inv.mileage.deleted'));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.failed')); }
  };

  const handleExportPdf = async () => {
    try {
      toast.info(t('inv.common.generatingPdf'));
      const response = await axios.get(`${API}/inventory/assets/${asset.id}/mileage/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`mileage_${asset.inventory_number || asset.id}.pdf`);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('inv.common.pdfExported'));
    } catch (err) { toast.error(t('inv.common.exportFailed')); }
  };

  const records = asset.mileage_records || [];

  return (
    <div className="space-y-3" data-testid="asset-mileage">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h4 className="text-sm font-semibold">{t('inv.mileage.title', { count: records.length })}</h4>
        <div className="flex gap-2">
          <DocLangToggle testId="doc-lang-assetmileage-0" />
          {records.length > 0 && (
            <Button size="sm" variant="outline" onClick={handleExportPdf} data-testid="export-mileage-pdf-btn">
              <FileDown className="w-4 h-4 mr-1" /> {t('inv.common.exportPdf')}
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-mileage-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('inv.mileage.addRecord')}
            </Button>
          )}
        </div>
      </div>
      {records.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.mileage.empty')}</p>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">#</TableHead>
              <TableHead>{t('inv.common.date')}</TableHead>
              <TableHead className="text-right">{t('inv.mileage.mileage')}</TableHead>
              <TableHead className="text-right">{t('inv.mileage.distance')}</TableHead>
              <TableHead>{t('inv.mileage.route')}</TableHead>
              <TableHead>{t('inv.mileage.purpose')}</TableHead>
              <TableHead>{t('inv.mileage.driver')}</TableHead>
              {isAdmin && <TableHead className="w-[40px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`mileage-row-${r.record_number}`}>
                <TableCell className="font-mono text-xs">{r.record_number}</TableCell>
                <TableCell className="text-sm">{r.date}</TableCell>
                <TableCell className="text-right text-sm">{r.current_mileage?.toLocaleString()}</TableCell>
                <TableCell className="text-right text-sm">{r.distance_covered?.toLocaleString()}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{[r.origin_address, r.destination_address].filter(Boolean).join(' → ') || '-'}</TableCell>
                <TableCell className="text-sm">{r.purpose || '-'}</TableCell>
                <TableCell className="text-sm">{r.driver_name || '-'}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(r.id)} data-testid={`delete-mileage-${r.record_number}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-mileage-dialog">
          <DialogHeader>
            <DialogTitle>{t('inv.mileage.dialogTitle')}</DialogTitle>
            <DialogDescription className="sr-only">{t('inv.mileage.dialogHint')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">{t('inv.common.date')} *</Label><Input type="date" value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))} required data-testid="mileage-date" /></div>
              <div><Label className="text-xs">{t('inv.mileage.currentMileage')} *</Label><Input type="number" step="0.1" value={form.current_mileage} onChange={(e) => setForm(prev => ({ ...prev, current_mileage: e.target.value }))} required data-testid="mileage-current" /></div>
              <div><Label className="text-xs">{t('inv.mileage.distance')} *</Label><Input type="number" step="0.1" value={form.distance_covered} onChange={(e) => setForm(prev => ({ ...prev, distance_covered: e.target.value }))} required data-testid="mileage-distance" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t('inv.mileage.from')}</Label><Input value={form.origin_address} onChange={(e) => setForm(prev => ({ ...prev, origin_address: e.target.value }))} /></div>
              <div><Label className="text-xs">{t('inv.mileage.to')}</Label><Input value={form.destination_address} onChange={(e) => setForm(prev => ({ ...prev, destination_address: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t('inv.mileage.purpose')}</Label><Input value={form.purpose} onChange={(e) => setForm(prev => ({ ...prev, purpose: e.target.value }))} /></div>
              <div><Label className="text-xs">{t('inv.mileage.driver')}</Label><Input value={form.driver_name} onChange={(e) => setForm(prev => ({ ...prev, driver_name: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button type="submit" data-testid="save-mileage-btn">{t('common.add')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetMileage;
